import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';

const STORAGE_KEY = 'dorat.partner.activeLocationId';

type ActiveLocationContextValue = {
	activeLocationId: string | null;
	setActiveLocationId: (id: string | null) => void;
};

const ActiveLocationContext = createContext<ActiveLocationContextValue | null>(null);

function readFromStorage(): string | null {
	if (typeof window === 'undefined') {
		return null;
	}

	try {
		return window.sessionStorage.getItem(STORAGE_KEY);
	} catch {
		return null;
	}
}

function writeToStorage(id: string | null) {
	if (typeof window === 'undefined') {
		return;
	}

	try {
		if (id) {
			window.sessionStorage.setItem(STORAGE_KEY, id);
		} else {
			window.sessionStorage.removeItem(STORAGE_KEY);
		}
	} catch {
		// sessionStorage unavailable (Safari private mode etc.) — fall back to in-memory only.
	}
}

/**
 * Provides the partner user's currently-selected location for scanning / equipment views.
 *
 * Client-only state, persisted to sessionStorage so it survives reloads within the
 * same browser tab. Cleared on logout via the auth subscriber that already wipes
 * `sessionStorage` on sign-out.
 */
export function PartnerActiveLocationProvider({ children }: { children: ReactNode }) {
	const [activeLocationId, setActiveLocationIdState] = useState<string | null>(() => readFromStorage());

	useEffect(() => {
		writeToStorage(activeLocationId);
	}, [activeLocationId]);

	const setActiveLocationId = useCallback((id: string | null) => {
		setActiveLocationIdState(id);
	}, []);

	const value = useMemo<ActiveLocationContextValue>(
		() => ({ activeLocationId, setActiveLocationId }),
		[activeLocationId, setActiveLocationId]
	);

	return <ActiveLocationContext.Provider value={value}>{children}</ActiveLocationContext.Provider>;
}

/**
 * Read or update the partner's currently-selected location.
 * Must be used inside a `PartnerActiveLocationProvider`.
 */
export function usePartnerActiveLocation(): ActiveLocationContextValue {
	const ctx = useContext(ActiveLocationContext);

	if (!ctx) {
		throw new Error('usePartnerActiveLocation must be used within PartnerActiveLocationProvider');
	}

	return ctx;
}
